<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { computed, watch } from 'vue'
import * as yup from 'yup'
import { type CreateServicePlanPayload, createServicePlanAction } from '@/api/functions/services/service-plan.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

interface ServicePlanRegion {
  region: string
  amount: number | null
  currency: string
}

const props = defineProps<{
  modelValue: boolean
  serviceSlug: string | undefined
  loading?: boolean
  countries: {
    label: string
    value: string
    flagUrl: string
    iso: string
  }[]
  currencies: {
    id: string
    isoCode: string
  }[]
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', value: CreateServicePlanPayload): void
  (e: 'refetch'): void
}>()

const toast = useToast()

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required('نام پلن را وارد کنید')
    .min(2, 'نام پلن باید حداقل 2 کاراکتر باشد')
    .max(100, 'نام پلن نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  duration: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .typeError('مدت اعتبار باید عدد باشد')
    .required('مدت اعتبار الزامی است')
    .integer('مدت اعتبار باید عدد صحیح باشد')
    .moreThan(0, 'مدت اعتبار باید بزرگ‌تر از صفر باشد'),

  sortOrder: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .typeError('ترتیب نمایش باید عدد باشد')
    .required('ترتیب نمایش الزامی است')
    .integer('ترتیب نمایش باید عدد صحیح باشد')
    .min(0, 'ترتیب نمایش نمی‌تواند منفی باشد'),

  description: yup.string().max(2000, 'توضیحات نمی‌تواند بیشتر از 2000 کاراکتر باشد'),

  status: yup.string().oneOf(['ACTIVE', 'INACTIVE'], 'وضعیت معتبر نیست').required('وضعیت الزامی است'),

  hasRegion: yup.boolean().required(),

  selectedRegions: yup
    .array()
    .of(yup.string().required())
    .when('hasRegion', {
      is: true,
      then: (schema) => schema.min(1, 'حداقل یک ریجن را انتخاب کنید').required('حداقل یک ریجن را انتخاب کنید'),
      otherwise: (schema) => schema,
    }),

  planAmount: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .when('hasRegion', {
      is: false,
      then: (schema) =>
        schema
          .typeError('مبلغ باید عدد باشد')
          .required('مبلغ را وارد کنید')
          .moreThan(0, 'مبلغ باید بزرگ‌تر از صفر باشد'),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),

  planCurrency: yup.string().when('hasRegion', {
    is: false,
    then: (schema) => schema.required('ارز پلن را انتخاب کنید'),
    otherwise: (schema) => schema.notRequired(),
  }),

  planRegions: yup
    .array()
    .of(
      yup.object({
        region: yup.string().required(),
        amount: yup
          .number()
          .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
          .typeError('مبلغ باید عدد باشد')
          .required('مبلغ این ریجن را وارد کنید')
          .moreThan(0, 'مبلغ باید بزرگ‌تر از صفر باشد'),
        currency: yup.string().required('ارز این ریجن را انتخاب کنید'),
      })
    )
    .when('hasRegion', {
      is: true,
      then: (schema) => schema.min(1, 'حداقل یک ریجن را انتخاب کنید').required(),
      otherwise: (schema) => schema,
    }),
})

const {
  handleSubmit,
  errors,
  resetForm: resetValidationForm,
  validateField,
  setFieldValue,
} = useForm({
  validationSchema: schema,
  initialValues: {
    name: '',
    duration: null as number | null,
    sortOrder: 0,
    description: '',
    status: 'ACTIVE',
    hasRegion: false,
    selectedRegions: [] as string[],
    planAmount: null as number | null,
    planCurrency: '',
    planRegions: [] as ServicePlanRegion[],
  },
})

const { value: planName } = useField<string>('name')
const { value: planDuration } = useField<number | null>('duration')
const { value: planSortOrder } = useField<number>('sortOrder')
const { value: planDescription } = useField<string>('description')
const { value: planStatus } = useField<string>('status')
const { value: hasRegion } = useField<boolean>('hasRegion')
const { value: selectedRegions } = useField<string[]>('selectedRegions')
const { value: planAmount } = useField<number | null>('planAmount')
const { value: planCurrency } = useField<string>('planCurrency')
const { value: planRegions } = useField<ServicePlanRegion[]>('planRegions')

const statusOptions = [
  { label: 'فعال', value: 'ACTIVE' },
  { label: 'غیرفعال', value: 'INACTIVE' },
]

const createPlanMutation = useMutationApi({
  mutationFn: (payload: CreateServicePlanPayload) => {
    if (!props.serviceSlug) {
      throw new Error('شناسه سرویس معتبر نیست')
    }

    return createServicePlanAction(props.serviceSlug, payload)
  },

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isSubmitting = computed(() => createPlanMutation.isPending.value || !!props.loading)

const resetForm = () => {
  resetValidationForm()
}

const closeModal = () => {
  if (isSubmitting.value) return

  emit('update:modelValue', false)
  resetForm()
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) {
      resetForm()
    }
  }
)

watch(hasRegion, (value) => {
  if (value) {
    setFieldValue('planAmount', null)
    setFieldValue('planCurrency', '')
  } else {
    setFieldValue('selectedRegions', [])
    setFieldValue('planRegions', [])
  }
})

const handleRegionsChange = (regions: string[]) => {
  const updatedRegions = regions.map((region) => {
    const existingRegion = planRegions.value.find((item) => item.region === region)

    return (
      existingRegion || {
        region,
        amount: null,
        currency: '',
      }
    )
  })

  setFieldValue('planRegions', updatedRegions)
  validateField('selectedRegions')
  validateField('planRegions')
}

const addPlan = handleSubmit(async (values) => {
  const prices: CreateServicePlanPayload['prices'] = values.hasRegion
    ? values.planRegions.map((region) => ({
        pricing_type: 'fixed',
        region_id: region.region,
        currency_id: region.currency,
        amount: String(region.amount),
      }))
    : [
        {
          pricing_type: 'fixed',
          region_id: null,
          currency_id: values.planCurrency,
          amount: String(values.planAmount),
        },
      ]

  const payload: CreateServicePlanPayload = {
    name: values.name.trim(),
    description: values.description.trim() || null,
    duration_days: Number(values.duration),
    sort_order: Number(values.sortOrder),
    is_active: values.status === 'ACTIVE',
    prices,
  }

  try {
    await createPlanMutation.mutateAsync(payload)

    emit('confirm', payload)
    emit('refetch')
    emit('update:modelValue', false)

    resetForm()
    toast.success('پلن با موفقیت ایجاد شد')
  } catch {}
})
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="emit('update:modelValue', $event)" width="700px"
        background="#FFF" :closable="!isSubmitting" title="افزودن پلن" closeDirection="left">
        <form @submit.prevent="addPlan" class="flex flex-col min-h-[600px]">
            <div class="flex-1 overflow-y-auto px-4 py-6 pb-9">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Input v-model="planName" name="name" label="نام پلن" type="text" :error="errors.name" />

                    <Input v-model="planDuration" name="duration" label="مدت اعتبار (روز)" type="number"
                        :error="errors.duration" />

                    <Input v-model="planSortOrder" name="sortOrder" label="ترتیب نمایش" type="number"
                        :error="errors.sortOrder" />

                    <Select v-model="planStatus" name="status" label="وضعیت" :options="statusOptions"
                        :error="errors.status" />

                    <div class="lg:col-span-2">
                        <Textarea v-model="planDescription" name="description" label="توضیحات پلن" type="text"
                            :error="errors.description" />
                    </div>

                    <div class="lg:col-span-2 mt-16 mb-2">
                        <Checkbox v-model="hasRegion" name="hasRegion" label="این پلن دارای ریجن است" color="#1b325a"
                            labelColor="#1b325a" />
                    </div>

                    <template v-if="!hasRegion">
                        <div>
                            <Input v-model="planAmount" name="planAmount" label="مبلغ" type="number"
                                :error="errors.planAmount" />
                        </div>

                        <div>
                            <Select v-model="planCurrency" name="planCurrency" label="ارز" :options="currencies"
                                :error="errors.planCurrency" />
                        </div>
                    </template>

                    <template v-else>
                        <div class="lg:col-span-2">
                            <MultiSelect v-model="selectedRegions" variant="checkbox" name="selectedRegions"
                                label="ریجن" :options="countries" @update:modelValue="handleRegionsChange" />

                            <p v-if="errors.selectedRegions" class="text-sm text-red-500 mt-1">
                                {{ errors.selectedRegions }}
                            </p>
                        </div>

                        <div v-for="(region, index) in planRegions" :key="region.region"
                            class="lg:col-span-2 grid lg:grid-cols-2 gap-6 p-4 rounded-xl bg-[#f7f9fc] border border-[#e5eaf2]">
                            <Input v-model="region.amount" :name="`planRegions[${index}].amount`"
                                :label="`مبلغ ریجن ${countries.find(item => item.value === region.region)?.label ?? ''}`" />

                            <Select v-model="region.currency" :name="`planRegions[${index}].currency`"
                                :label="`ارز ریجن ${countries.find(item => item.value === region.region)?.label ?? ''}`"
                                :options="currencies" />
                        </div>
                    </template>
                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    :disabled="isSubmitting" class="w-4/12 h-12 rounded-xl border-2" />
                <Button type="submit" label="افزودن پلن" :isAdmin="true" :loading="isSubmitting"
                    :disabled="isSubmitting" class="w-9/12 h-12 rounded-xl" />

            </div>
        </form>
    </Modal>
</template>